import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import React from 'react'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { formatStyles } from '../../components/react-intl-helpers'
import { useIntl } from 'react-intl'

interface Props {
  title: string
  subtitle?: React.ReactNode
  children: React.ReactNode
  cornerContent?: React.ReactNode
}

export function ConfigurationPanel({
  title,
  subtitle,
  children,
  cornerContent
}: Props) {
  return (
    <div
      className={cx(
        'legacy-ds-panel',
        css`
          display: flex;
          flex-direction: column;
          border: 1px solid ${theme.color.neutral40};
          border-radius: 8px;
          box-shadow: 0px 2px 4px rgba(41, 41, 41, 0.2);
          overflow: hidden;
        `
      )}
    >
      <PanelHeader
        titleI18n={title}
        subtitle={subtitle}
        cornerContent={cornerContent}
      />
      <div
        className={cx(
          'legacy-ds-panel-body',
          css`
            display: flex;
            flex-direction: column;
            background: ${theme.color.white};
          `
        )}
      >
        {children}
      </div>
    </div>
  )
}

export const PanelHeader = ({
  titleI18n,
  subtitle,
  cornerContent
}: {
  titleI18n: string
  subtitle?: React.ReactNode
  cornerContent?: React.ReactNode
}) => {
  const { formatMessage } = useIntl()

  return (
    <div
      className={cx(
        'legacy-ds-panel-header',
        css`
          align-items: center;
          background: ${theme.color.neutral20};
          border-bottom: 1px solid ${theme.color.neutral40};
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          display: flex;
          height: 70px;
          padding: 16px 24px;
        `
      )}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <Text font="titleH5" color="neutral100">
          {formatMessage({ id: titleI18n })}
        </Text>

        {subtitle}
      </div>

      {cornerContent && (
        <div
          className={css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
            align-self: center;
          `}
        >
          {cornerContent}
        </div>
      )}
    </div>
  )
}

export const PanelSubtitle = ({
  subtitleI18n,
  linkI18n
}: {
  subtitleI18n
  linkI18n
}) => {
  const { formatMessage } = useIntl()

  return (
    <Text as="div" font="metadata" color="neutral70">
      {formatMessage(
        { id: subtitleI18n },
        {
          ...formatStyles.anchor,
          a: (...msg) => (
            <Link
              href={formatMessage({
                id: linkI18n
              })}
              isExternal
            >
              {msg}
            </Link>
          )
        }
      )}
    </Text>
  )
}
